/*
  C Home Vibe - the moving vibe card of sections/c-home-feel-music.liquid (homepage A/B, section 09).
  ClickUp: https://app.clickup.com/t/123y1w8fdpr

  Motion spec: the design team's "Vibe card motion" study. Seven nested curves each sway on their own
  8-18s cycle over a 24s drift of the group; two soft pools, a warm band, a deep diagonal and a warm light
  travel on 13-21s cycles; a light sweeps the card's edge every 4.2s. Every value below is the study's.

  Why a canvas in a worker, not the study's CSS on SVG groups: a transform on an SVG child is not
  composited, so the whole card re-rasterises and re-composites every frame, at full pixel density and
  60fps. Measured in headless Chromium (software raster, the slow-GPU worst case), 1440 at 2x:
    - static card: compositor 1%. The SVG study: compositor 66%, raster 24%.
    - the same drawing on a main-thread canvas: main thread 49-60% with the CPU throttled 4x.
    - this worker: main thread as for the static card; worker 1%, raster 0-6%, compositor 5%.
  It draws on an OffscreenCanvas in a worker, so the page's main thread does no per-frame work, at up to
  1.5x density and 30fps: the artwork is soft gradients, and the curves move 0.1-0.4px per frame at 30fps.
  If drawing costs too much, or frames arrive late (how a slow GPU shows), it steps down (1x, then 20fps,
  then 0.75x) and, as a last resort, stops and leaves the static card.

  The static card is the inline SVG under the canvas: the same first frame, so the canvas fades in over
  it without a visible change. It is all that shows under reduced motion, without OffscreenCanvas in
  workers (before Chrome 69, Firefox 105, Safari 16.4), and on a device too slow for the last step.

*/
(function () {
  'use strict';

  // Everything the worker runs lives in this function: the page turns its source into a Blob URL, so the
  // worker needs no network request and works whichever domain serves the theme's assets.
  function vibeWorker() {
    'use strict';

    var W = 820;
    var H = 266;
    var RAD = Math.PI / 180;

    // Curves, outermost first: [path, tx, ty, fill, opacity, duration, rotate, scale, delay, originX%, originY%].
    // Paths use only M and C, as flat number lists.
    var BLOBS = [
      [[1997.7,771.4, 2063.6,1186.9,1693.9,1796.7,1081.4,1365.8, 468.9,934.8,71,1367,5.1,951.6, -60.8,536.1,528.1,468.2,882.2,109.3, 1236.1,-249.7,1931.9,356,1997.7,771.4], -691.8, -710.6, '#C9A188', 0.3, 18, 5.5, 1.1, 0, 30, 120],
      [[1556.5,585.5, 1622.7,908.7,1355.4,1392.4,861.7,1068.1, 368.1,743.8,72.6,1089.5,6.5,766.3, -59.7,443.1,398,378.3,661.8,90.8, 925.6,-196.8,1490.4,262.3,1556.5,585.5], -438.1, -639.2, '#8CA07E', 0.3, 16, -6.5, 1.09, -2.3, 70, 110],
      [[1364.4,513.2, 1422.3,796.5,1188,1220.5,755.4,936.3, 322.7,652,63.6,955,5.7,671.7, -52.3,388.4,348.9,331.6,580.1,79.6, 811.3,-172.5,1306.5,230,1364.4,513.3], -342.7, -595, '#C09A86', 0.22, 14, 7, 1.1, -4.6, 20, 130],
      [[1185.8,430.6, 1250.9,676.2,1068,1053.1,675.8,817.6, 283.7,582.1,73.1,854,8,608.5, -57.1,362.9,290.2,301.6,479.1,75, 668.1,-151.7,1120.7,185.1,1185.8,430.6], -224.3, -557.5, '#6E8C66', 0.32, 12.5, -7.5, 1.1, -6.9, 80, 120],
      [[1003.1,364.3, 1058.2,572,903.5,890.8,571.7,691.6, 240,492.4,61.8,722.5,6.8,514.7, -48.3,307,245.5,255.2,405.3,63.4, 565.1,-128.3,948,156.6,1003.1,364.3], -132.7, -521.9, '#537553', 0.3, 11, 8.5, 1.12, -9.2, 40, 140],
      [[782.7,284.3, 825.6,446.4,705,695.1,446.1,539.7, 187.3,384.2,48.2,563.7,5.3,401.7, -37.6,239.6,191.5,199.1,316.3,49.5, 441,-100.2,739.8,122.2,782.7,284.3], -16.1, -490, '#446B4C', 0.28, 9.5, -9, 1.11, -11.5, 60, 130],
      [[598.1,217.2, 631,341.1,538.7,531.2,340.9,412.4, 143.1,293.6,36.9,430.8,4,306.9, -28.8,183.1,146.3,152.2,241.7,37.8, 337,-76.5,565.3,93.4,598.1,217.2], 85, -454.2, '#37603F', 0.3, 8, 10, 1.12, -13.8, 50, 150]
    ];
    // The diagonal, in its bounding box's unit space (560..980 x -140..320): its gradient is objectBoundingBox.
    var DIAG = [0, 0.04348, 0.38095, 0.34783, 0.33333, 0.69565, 0.80952, 1];

    // Steps, best first: max pixel density, frames per second.
    var STEPS = [[1.5, 30], [1, 30], [1, 20], [0.75, 20]];
    var SWEEP_PERIOD = 4.2;

    // CSS ease-in-out, cubic-bezier(.42, 0, .58, 1).
    function ease(x) {
      var t = x;
      for (var i = 0; i < 6; i++) {
        var fx = ((0.52 * t - 0.78) * t + 1.26) * t - x;
        var d = (1.56 * t - 1.56) * t + 1.26;
        if (Math.abs(fx) < 1e-5 || !d) break;
        t -= fx / d;
      }
      return (-2 * t + 3) * t * t;
    }

    // Eased progress of an `infinite alternate` animation at time t (seconds).
    function phase(t, duration, delay) {
      var p = (t - delay) / duration;
      var i = Math.floor(p);
      var f = p - i;
      return ease(i % 2 ? 1 - f : f);
    }

    function lerp(a, b, e) {
      return a + (b - a) * e;
    }

    function roundRect(c, x, y, w, h, r) {
      c.beginPath();
      c.moveTo(x + r, y);
      c.lineTo(x + w - r, y);
      c.arc(x + w - r, y + r, r, -Math.PI / 2, 0);
      c.lineTo(x + w, y + h - r);
      c.arc(x + w - r, y + h - r, r, 0, Math.PI / 2);
      c.lineTo(x + r, y + h);
      c.arc(x + r, y + h - r, r, Math.PI / 2, Math.PI);
      c.lineTo(x, y + r);
      c.arc(x + r, y + r, r, Math.PI, Math.PI * 1.5);
      c.closePath();
    }

    function Renderer(canvas, ctx) {
      this.canvas = canvas;
      this.c = ctx;
      this.cssW = W;
      this.cssH = H;
      this.scale = 1;
      this.gradients();
    }

    Renderer.prototype.size = function (cssW, cssH, scale) {
      this.cssW = cssW;
      this.cssH = cssH;
      this.scale = scale;
      this.canvas.width = Math.max(1, Math.round(cssW * scale));
      this.canvas.height = Math.max(1, Math.round(cssH * scale));
    };

    Renderer.prototype.gradients = function () {
      var c = this.c;
      var g = c.createLinearGradient(0, 0, 0, H);
      g.addColorStop(0, '#CBA68E');
      g.addColorStop(0.26, '#9A9A7C');
      g.addColorStop(0.62, '#78876A');
      g.addColorStop(1, '#547053');
      this.gBase = g;
      // A pool is a unit circle, stretched to its ellipse when drawn.
      g = c.createRadialGradient(0, 0, 0, 0, 0, 1);
      g.addColorStop(0, 'rgba(47,90,59,0.55)');
      g.addColorStop(1, 'rgba(47,90,59,0)');
      this.gPool = g;
      // The band's own vertical gradient (its rect is x -140, y -300, 1120 x 420).
      g = c.createLinearGradient(0, -300, 0, 120);
      g.addColorStop(0, 'rgba(214,169,145,0.8)');
      g.addColorStop(0.62, 'rgba(214,169,145,0.34)');
      g.addColorStop(1, 'rgba(214,169,145,0)');
      this.gBand = g;
      g = c.createLinearGradient(0, 0, 1, 0.6);
      g.addColorStop(0, 'rgba(47,90,59,0.3)');
      g.addColorStop(1, 'rgba(47,90,59,0.55)');
      this.gDiag = g;
      g = c.createLinearGradient(0.15, 0, 0.85, 0.7);
      g.addColorStop(0, 'rgba(221,182,155,0)');
      g.addColorStop(0.55, 'rgba(221,182,155,0.38)');
      g.addColorStop(1, 'rgba(221,182,155,0)');
      this.gWarm = g;
    };

    Renderer.prototype.draw = function (t) {
      var c = this.c;
      var k = this.scale;
      // The artwork is 820 x 266, centred and cropped to the card (preserveAspectRatio slice).
      var s = Math.max(this.cssW / W, this.cssH / H) * k;
      var e;
      var i;
      c.setTransform(s, 0, 0, s, (this.cssW * k - W * s) / 2, (this.cssH * k - H * s) / 2);
      c.globalAlpha = 1;
      c.fillStyle = this.gBase;
      c.fillRect(0, 0, W, H);

      // The group drift (24s) around 50% 60%, and each curve's sway around its own pivot below the card.
      c.save();
      e = phase(t, 24, 0);
      c.translate(410, 159.6);
      c.translate(lerp(-26, 30, e), lerp(12, -16, e));
      c.scale(lerp(1, 1.09, e), lerp(1, 1.09, e));
      c.translate(-410, -159.6);
      for (i = 0; i < BLOBS.length; i++) {
        var b = BLOBS[i];
        var p = b[0];
        var sw = phase(t, b[5], b[8]);
        var ox = (b[9] / 100) * W;
        var oy = (b[10] / 100) * H;
        c.save();
        c.translate(ox, oy);
        c.rotate(b[6] * sw * RAD);
        c.scale(lerp(1, b[7], sw), lerp(1, b[7], sw));
        c.translate(b[1] - ox, b[2] - oy);
        c.beginPath();
        c.moveTo(p[0], p[1]);
        for (var j = 2; j < p.length; j += 6) c.bezierCurveTo(p[j], p[j + 1], p[j + 2], p[j + 3], p[j + 4], p[j + 5]);
        c.closePath();
        c.globalAlpha = b[4];
        c.fillStyle = b[3];
        c.fill();
        c.restore();
      }
      c.restore();
      c.globalAlpha = 1;

      // Two soft pools (15s and 13s).
      this.pool(t, 760, 250, 240, 190, 15, 0);
      this.pool(t, 120, 300, 190, 130, 13, -11);

      // The warm band across the top (21s), around 50% 0.
      c.save();
      e = phase(t, 21, 0);
      c.translate(410, 0);
      c.translate(lerp(-44, 48, e), lerp(-22, 30, e));
      c.scale(lerp(1.02, 1.1, e), lerp(1.02, 1.1, e));
      c.rotate(lerp(-1.2, 1.4, e) * RAD);
      c.translate(0, 40);
      c.rotate(-5 * RAD);
      c.translate(-410, -40);
      c.fillStyle = this.gBand;
      roundRect(c, -140, -300, 1120, 420, 140);
      c.fill();
      c.restore();

      // The deep green diagonal (19s), around 80% 40%.
      c.save();
      e = phase(t, 19, -9);
      c.translate(656, 106.4);
      c.translate(lerp(-46, 50, e), lerp(-6, 16, e));
      c.rotate(lerp(-4, 5, e) * RAD);
      c.scale(lerp(1, 1.08, e), lerp(1, 1.08, e));
      c.translate(-656 + 560, -106.4 - 140);
      c.scale(420, 460);
      c.fillStyle = this.gDiag;
      c.beginPath();
      c.moveTo(DIAG[0], DIAG[1]);
      c.bezierCurveTo(DIAG[2], DIAG[3], DIAG[4], DIAG[5], DIAG[6], DIAG[7]);
      c.lineTo(1, 0);
      c.closePath();
      c.fill();
      c.restore();

      // The warm light (17s).
      c.save();
      e = phase(t, 17, 0);
      c.translate(lerp(-60, 64, e) - 80, lerp(-14, 22, e) - 70);
      c.scale(980, 210);
      c.fillStyle = this.gWarm;
      c.fillRect(0, 0, 1, 1);
      c.restore();

      this.sweep(t);
    };

    Renderer.prototype.pool = function (t, cx, cy, rx, ry, duration, delay) {
      var c = this.c;
      var e = phase(t, duration, delay);
      c.save();
      c.translate(cx, cy);
      c.translate(lerp(-34, 38, e), lerp(18, -22, e));
      c.scale(lerp(0.9, 1.3, e), lerp(0.9, 1.3, e));
      c.scale(rx, ry);
      c.fillStyle = this.gPool;
      c.beginPath();
      c.arc(0, 0, 1, 0, Math.PI * 2);
      c.fill();
      c.restore();
    };

    // A light running round the card's edge every 4.2s, in CSS pixels so its corners follow the card's
    // 64px radius at every width: a dash 18% of the perimeter long, glowing.
    Renderer.prototype.sweep = function (t) {
      var c = this.c;
      var k = this.scale;
      var w = this.cssW - 2;
      var h = this.cssH - 2;
      var r = Math.min(64, h / 2, w / 2);
      var perimeter = 2 * (w - 2 * r) + 2 * (h - 2 * r) + 2 * Math.PI * r;
      var f = (t % SWEEP_PERIOD) / SWEEP_PERIOD;
      c.setTransform(k, 0, 0, k, 0, 0);
      c.setLineDash([perimeter * 0.18, perimeter]);
      c.lineDashOffset = perimeter * 1.14 * (1 - f);
      c.lineCap = 'butt';
      roundRect(c, 1, 1, w, h, r);
      c.shadowColor = 'rgba(255,235,220,0.85)';
      c.shadowBlur = 8 * k;
      c.strokeStyle = 'rgba(255,245,235,0.9)';
      c.lineWidth = 2;
      c.stroke();
      c.shadowBlur = 0;
      c.shadowColor = 'transparent';
      c.setLineDash([]);
    };

    // The clock, the frame pacing and the step-down. `host` schedules frames and reports back.
    function Engine(renderer, host, firstStep) {
      this.r = renderer;
      this.host = host;
      this.step = firstStep || 0;
      this.clock = 0;
      this.prev = 0;
      this.last = 0;
      this.cost = 0;
      this.late = 0;
      this.frames = 0;
      this.running = false;
      this.drawn = false;
      this.dpr = 1;
      this.cssW = W;
      this.cssH = H;
    }

    Engine.prototype.resize = function (cssW, cssH, dpr) {
      this.cssW = cssW;
      this.cssH = cssH;
      this.dpr = dpr;
      this.r.size(cssW, cssH, Math.min(dpr, STEPS[this.step][0]));
      // Resizing clears the canvas: draw straight away so no empty frame reaches the screen.
      this.r.draw(this.clock);
    };

    Engine.prototype.run = function (on) {
      if (on === this.running) return;
      this.running = on;
      this.prev = 0;
      this.last = 0;
      if (on) this.host.schedule();
    };

    Engine.prototype.frame = function (now) {
      if (!this.running) return;
      this.host.schedule();
      var fps = STEPS[this.step][1];
      if (this.last && now - this.last < 1000 / fps - 4) return;
      var interval = this.last ? now - this.last : 0;
      this.last = now;
      // A paused card resumes where it stopped: the clock only advances while frames are drawn.
      this.clock += this.prev ? Math.min(now - this.prev, 100) / 1000 : 0;
      this.prev = now;
      var start = performance.now();
      this.r.draw(this.clock);
      var took = performance.now() - start;
      if (!this.drawn) {
        this.drawn = true;
        this.host.report('drawn');
      }
      this.frames++;
      this.cost = this.frames === 1 ? took : this.cost * 0.85 + took * 0.15;
      // A slow GPU shows as frames arriving late rather than as drawing time, so both count: drawing over
      // half a 30fps frame, or frames that keep arriving at under 1/1.75 of the target rate (below 17fps
      // at 30, below 11fps at 20). Low Power Mode's 30Hz cap on iOS stays within that.
      if (interval) this.late = this.late * 0.85 + (interval > 1750 / fps ? 0.15 : 0);
      if (this.frames > 12 && (this.cost > 16 || this.late > 0.4)) this.stepDown();
    };

    Engine.prototype.stepDown = function () {
      this.frames = 0;
      this.cost = 0;
      this.late = 0;
      if (this.step >= STEPS.length - 1) {
        this.running = false;
        this.host.report('slow');
        return;
      }
      this.step++;
      this.last = 0;
      this.r.size(this.cssW, this.cssH, Math.min(this.dpr, STEPS[this.step][0]));
      this.r.draw(this.clock);
    };

    // ---- Worker messages ----
    var engine = null;
    var queued = false;
    var raf = self.requestAnimationFrame
      ? function (fn) { self.requestAnimationFrame(fn); }
      : function (fn) { setTimeout(function () { fn(performance.now()); }, 16); };
    var host = {
      schedule: function () {
        if (queued) return;
        queued = true;
        raf(function (now) {
          queued = false;
          if (engine) engine.frame(now);
        });
      },
      report: function (type) { self.postMessage({ type: type }); }
    };
    self.onmessage = function (event) {
      var m = event.data;
      if (m.canvas) {
        var ctx = m.canvas.getContext('2d', { alpha: false });
        engine = new Engine(new Renderer(m.canvas, ctx), host, 0);
        engine.resize(m.w, m.h, m.dpr);
      }
      if (!engine) return;
      if (m.size) engine.resize(m.w, m.h, m.dpr);
      if (typeof m.run === 'boolean') engine.run(m.run);
    };
  }

  // ---- Page side ----
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var workerUrl = null;

  function workerSource() {
    if (!workerUrl) workerUrl = URL.createObjectURL(new Blob(['(' + vibeWorker.toString() + ')();'], { type: 'text/javascript' }));
    return workerUrl;
  }

  function start(root) {
    if (root.dataset.vibe) return;
    var canvas = root.querySelector('canvas');
    if (!canvas || !('IntersectionObserver' in window)) return;
    root.dataset.vibe = 'on';

    var inView = false;
    var control;

    function wanted() {
      return inView && !document.hidden && !reduceMotion.matches;
    }

    function measure() {
      var box = canvas.getBoundingClientRect();
      return { w: box.width, h: box.height, dpr: window.devicePixelRatio || 1 };
    }

    function report(type) {
      if (type === 'drawn') root.classList.add('is-drawn');
      if (type === 'slow') {
        root.classList.remove('is-drawn');
        root.dataset.vibe = 'static';
      }
    }

    function worker() {
      var w;
      try {
        if (!window.Worker || !window.Blob || !canvas.transferControlToOffscreen) return null;
        w = new Worker(workerSource());
        var off = canvas.transferControlToOffscreen();
        var size = measure();
        w.postMessage({ canvas: off, w: size.w, h: size.h, dpr: size.dpr }, [off]);
      } catch (error) {
        if (w) w.terminate();
        return null;
      }
      w.onmessage = function (event) {
        report(event.data.type);
        if (event.data.type === 'slow') w.terminate();
      };
      // A worker that cannot draw (no 2D context in workers) leaves the static card showing.
      w.onerror = function () {
        report('slow');
        w.terminate();
      };
      return {
        run: function (on) { w.postMessage({ run: on }); },
        size: function (s) { w.postMessage({ size: true, w: s.w, h: s.h, dpr: s.dpr }); }
      };
    }

    function update() {
      if (root.dataset.vibe === 'static') return;
      if (!control && wanted()) {
        control = worker();
        // No worker canvas here: the static card stays.
        if (!control) root.dataset.vibe = 'static';
      }
      if (control) control.run(wanted());
    }

    new IntersectionObserver(
      function (entries) {
        inView = entries[0].isIntersecting;
        update();
      },
      { rootMargin: '100px 0px' }
    ).observe(root);

    if ('ResizeObserver' in window) {
      var width = 0;
      new ResizeObserver(function () {
        if (!control) return;
        var s = measure();
        if (Math.abs(s.w - width) < 1) return;
        width = s.w;
        control.size(s);
      }).observe(canvas);
    }

    document.addEventListener('visibilitychange', update);
    if (reduceMotion.addEventListener) reduceMotion.addEventListener('change', update);
  }

  function init(scope) {
    Array.prototype.forEach.call((scope || document).querySelectorAll('[data-c-home-vibe]'), start);
  }

  init();
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
